import {observable, action, toJS} from 'mobx';
import {GALLERY, UPLOAD} from "./types";
import socialGalleryApi from './api';
import _ from 'lodash';
import SessionStore from '../../services/session/SessionStore';
import {isVideo} from "../../modules/social/publishing";
import {isImage} from "./extensions";

const PAGING_LIMIT = 12;

class UploadGalleryStore {

  /* MobX Observable Properties. */

  @observable uploadActive = false;
  @observable uploadProgress = 0; // 0 = lowest value, 1 = highest value.
  @observable lastError = false;
  @observable dropRejected = false;
  @observable activeTab = GALLERY; // gallery or upload
  @observable galleryFiles = [];
  @observable uploadOffset = 0;
  @observable searchText = '';
  @observable selectedFile = {};

  sessionStore = SessionStore;
  imageSelectedCallback;
  imageUploadedErrorCallback = console.warn;
  galleryApi;

  constructor(galleryApi) {
    // You can inject other api implementations to save/upload to other image collections (social/reviewbuilder/etc)
    // Defaults to social because I stole this from social
    this.galleryApi = galleryApi || socialGalleryApi;
    this.getExistingImages(this.uploadOffset);
  }

  /* MobX Actions */

  @action setActiveTab(name) {
    this.activeTab = name;
  }

  @action toggleDropRejected() {
    this.dropRejected = !this.dropRejected;
  }

  @action toggleActiveTab() {
    if (this.activeTab === GALLERY) {
      this.activeTab = UPLOAD;
    } else {
      this.activeTab = GALLERY;
    }
  }

  @action setGalleryFiles(files) {
    // Sort by most recent first.
    this.galleryFiles = this.galleryFiles.concat(files).sort(function(a, b) { return b.dateAdded-a.dateAdded });
  }

  @action getExistingImages = _.debounce((offset) => {
    return this.galleryApi.getChatterUploads({offset, limit: PAGING_LIMIT}).then(this.getExistingImagesSuccess);
  }, 200);

  @action.bound getExistingImagesSuccess(response) {
    this.setGalleryFiles(response);
    this.setUploadOffset(response.length);
    return response;
  }

  @action setUploadOffset(offset) {
    this.uploadOffset = this.uploadOffset + offset;
  }

  @action setSearchText(text) {
    this.searchText = text;
  }

  @action setSelectedFileByIndex(index) {
    if (this.galleryFiles.length > 0) {
      const selectedFile = this.galleryFiles[index];
      if (selectedFile === null || this.selectedFile === selectedFile) {
        this.setSelectedFile({});
      } else {
        this.setSelectedFile(selectedFile);
      }
    }
  }

  @action setSelectedFile(file) {
    const type = isImage(file.url) ? 'Image' : isVideo(file.url) ? 'Video' : undefined;
    this.selectedFile = {...file, type: type};
    if (this.imageSelectedCallback) {
      this.imageSelectedCallback(this.selectedFile);
    }
  }

  @action deleteChatterUpload = _.debounce((item) => {
    if (this.galleryFiles.length > 0) {
      if (item !== null) {
        // Unfortunately the apis use different formats for ids
        const itemId = item._id.$oid || item._id;
        this.galleryApi.deleteChatterUpload(itemId);
        this.galleryFiles = this.galleryFiles.filter(x => {
          const searchItemId = x._id.$oid || x._id;
          return searchItemId !== itemId
        });
      }
    }
  }, 200);

  @action saveChatterUpload = _.debounce((file) => {
    if (this.uploadActive) {
      throw new Error("upload is already in progress")
    }
    this.uploadActive = true;

    const accountId = this.sessionStore.account.accountId;
    const formData = new FormData();
    formData.append('file', file);

    return this.galleryApi.saveChatterUpload(accountId, formData, (e) => {
      this.uploadProgress = e.loaded / e.total; // Number between 0 and 1. 1 = 100% uploaded.
    })
    .then(this.saveChatterUploadSuccess)
    .catch(this.saveChatterUploadError);
  }, 200);

  @action.bound saveChatterUploadSuccess(response) {
    const uploadURL = response.url || response;
    this.galleryApi.getChatterUploads({offset: 0, limit: 1}).then((uploadsResponse) => {
      const filtered = uploadsResponse.filter(f => f.url === uploadURL);
      if (filtered.length === 1) {
        // Not updating the galleryFiles here because UploadGallery should be dismissed after an image is selected.
        this.setSelectedFile(filtered[0]);
      } else {
        // Only used for bulk editor, not for social gallery
        this.setSelectedFile({url: uploadURL});
      }
    });
    this.uploadActive = false;
    this.uploadProgress = 0;
    return response;
  }

  @action.bound saveChatterUploadError(error) {
    const errorMessage = error.response.data;

    this.uploadActive = false;
    this.uploadProgress = 0;
    this.imageUploadedErrorCallback(errorMessage);
  }

  setImageSelectedCallback(callback) {
    this.imageSelectedCallback = callback;
  }

  setImageUploadedErrorCallback(callback) {
    this.imageUploadedErrorCallback = callback;
  }
}

export default UploadGalleryStore;
